import React, { useEffect, useState } from 'react';
import { Building2, CreditCard, AlertCircle, CheckCircle, Save } from 'lucide-react';
import api from '../services/api';

/**
 * /org-settings — the client_admin's view of *their own* organization.
 *
 * This deliberately shows a SINGLE org (the one the user belongs to), not a
 * list. It replaces the previous mistake of routing client_admin to the
 * platform-wide Organizations tab, which exposed every other tenant's row.
 */
export default function OrgSettings() {
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const r = await api.get('/organizations/me');
      setOrg(r.data);
      setForm({
        organizationName: r.data?.organizationName || '',
        industry: r.data?.industry || '',
        headquarters: r.data?.headquarters || '',
        businessModel: r.data?.businessModel || '',
        contactDetails: r.data?.contactDetails || { name: '', email: '', phone: '' },
      });
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not load your organization.');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    setSubmitting(true);
    try {
      await api.patch('/organizations/me', form);
      setSuccess('Organization updated.');
      setTimeout(() => setSuccess(''), 2500);
      load();
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not update.');
    } finally { setSubmitting(false); }
  };

  if (loading) return <div className="rounded-xl border border-black/10 bg-ink-900 p-8 text-center text-ink-400">Loading…</div>;
  if (!org) return <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-4 text-red-300">{error || 'Organization not found.'}</div>;

  const used = org.interviews_used || 0;
  const quota = org.interview_quota;
  const pct = quota ? Math.min(100, Math.round((used / quota) * 100)) : 0;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-extrabold text-ink-100">Organization</h2>
        <p className="mt-1 text-sm text-ink-400">
          Your organization's profile, contact, and usage. Only you (the client admin) can edit these fields.
        </p>
      </div>

      {/* Identity card */}
      <div className="rounded-2xl border border-black/10 bg-ink-900 p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-xs text-ink-500">
              <Building2 className="h-3.5 w-3.5" /> Organization ID
            </div>
            <code className="mt-1 block text-xs text-ink-300">{org.organization_id}</code>
          </div>
          <div className="text-right">
            <div className="text-xs text-ink-500">Tenant created</div>
            <div className="text-sm text-ink-300">{org.created_at ? new Date(org.created_at).toLocaleDateString() : '—'}</div>
          </div>
        </div>
      </div>

      {/* Usage card */}
      <div className="rounded-2xl border border-black/10 bg-ink-900 p-6">
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm font-semibold text-ink-100">
            <CreditCard className="h-4 w-4 text-brand-400" /> Interview-minute usage
          </div>
          <div className="text-sm text-ink-300">
            {used} <span className="text-ink-500">/ {quota ?? '∞'}</span>
          </div>
        </div>
        <div className="h-2 w-full overflow-hidden rounded-full bg-black/5">
          <div className={`h-full ${pct > 90 ? 'bg-red-500' : pct > 70 ? 'bg-amber-500' : 'bg-brand-500'}`} style={{ width: `${pct}%` }} />
        </div>
        <p className="mt-2 text-xs text-ink-500">
          {quota == null ? 'Unlimited plan.' : `${quota - used} interview-minutes remaining.`}
          {' '}Top up under the Billing tab.
        </p>
      </div>

      {/* Edit form */}
      <form onSubmit={handleSave} className="rounded-2xl border border-black/10 bg-ink-900 p-6 space-y-4">
        <h3 className="text-lg font-semibold text-ink-100">Profile</h3>

        {error && (
          <div className="flex items-center gap-2 rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-300">
            <AlertCircle className="h-4 w-4" /> {error}
          </div>
        )}
        {success && (
          <div className="flex items-center gap-2 rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-3 text-sm text-emerald-300">
            <CheckCircle className="h-4 w-4" /> {success}
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="mb-1 block text-sm font-medium text-ink-300">Organization name</label>
            <input
              value={form.organizationName || ''}
              onChange={(e) => setForm({ ...form, organizationName: e.target.value })}
              className="input"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-ink-300">Industry</label>
            <input
              value={form.industry || ''}
              onChange={(e) => setForm({ ...form, industry: e.target.value })}
              className="input"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-ink-300">Headquarters</label>
            <input
              value={form.headquarters || ''}
              onChange={(e) => setForm({ ...form, headquarters: e.target.value })}
              className="input"
            />
          </div>
          <div className="col-span-2">
            <label className="mb-1 block text-sm font-medium text-ink-300">Business model</label>
            <input
              value={form.businessModel || ''}
              onChange={(e) => setForm({ ...form, businessModel: e.target.value })}
              className="input"
              placeholder="B2B SaaS / Services / Marketplace …"
            />
          </div>
        </div>

        <h3 className="pt-2 text-lg font-semibold text-ink-100">Primary contact</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-ink-300">Name</label>
            <input
              value={form.contactDetails?.name || ''}
              onChange={(e) => setForm({ ...form, contactDetails: { ...form.contactDetails, name: e.target.value } })}
              className="input"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-ink-300">Email</label>
            <input
              type="email"
              value={form.contactDetails?.email || ''}
              onChange={(e) => setForm({ ...form, contactDetails: { ...form.contactDetails, email: e.target.value } })}
              className="input"
            />
          </div>
          <div className="col-span-2">
            <label className="mb-1 block text-sm font-medium text-ink-300">Phone</label>
            <input
              value={form.contactDetails?.phone || ''}
              onChange={(e) => setForm({ ...form, contactDetails: { ...form.contactDetails, phone: e.target.value } })}
              className="input"
            />
          </div>
        </div>

        <div className="flex justify-end pt-2">
          <button type="submit" disabled={submitting} className="btn-primary inline-flex items-center gap-2">
            <Save className="h-4 w-4" /> {submitting ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </form>
    </div>
  );
}
